import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Clock, CheckCircle2, XCircle, FileText, DollarSign, Lock,
  ChevronDown, ChevronUp, Camera, MessageSquare, Package,
  ClipboardCheck, Droplets, Receipt, MapPin, Star, Search,
  Filter, ArrowLeft, Loader2
} from "lucide-react";
import { toast } from "sonner";
import JobReportPDF from "@/components/JobReportPDF";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  new: { label: "New", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  offered: { label: "Offered", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  accepted: { label: "Accepted", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  en_route: { label: "On Route", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  in_progress: { label: "In Progress", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed: { label: "Closed (Paid)", cls: "bg-zinc-800 text-white border-zinc-700" },
  canceled: { label: "Canceled", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  passed: { label: "Passed", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  missed: { label: "Missed", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

export default function History() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/requests/history?limit=200");
      setJobs(data.requests);
      setTotal(data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const loadJobDetail = async (jobId) => {
    if (expandedJob === jobId) { setExpandedJob(null); setJobDetail(null); return; }
    setExpandedJob(jobId);
    setLoadingDetail(true);
    try {
      const [detailRes, photosRes, receiptsRes, expensesRes, inventoryRes, checklistRes, chatRes, moistureRes] = await Promise.all([
        API.get(`/requests/${jobId}`),
        API.get(`/photos/job/${jobId}`).catch(() => ({ data: { photos: [] } })),
        API.get(`/photos/receipts/job/${jobId}`).catch(() => ({ data: { receipts: [] } })),
        API.get(`/expenses/job/${jobId}`).catch(() => ({ data: { expenses: [] } })),
        API.get(`/inventory/job/${jobId}`).catch(() => ({ data: { items: [] } })),
        API.get(`/checklists/job/${jobId}`).catch(() => ({ data: null })),
        API.get(`/chat/${jobId}/messages`).catch(() => ({ data: { messages: [] } })),
        API.get(`/moisture/job/${jobId}`).catch(() => ({ data: { exists: false } })),
      ]);
      setJobDetail({
        ...detailRes.data,
        photos: photosRes.data.photos || [],
        receipts: receiptsRes.data.receipts || [],
        expenses: expensesRes.data.expenses || [],
        inventory: inventoryRes.data.items || [],
        inventoryCost: inventoryRes.data.total_cost || 0,
        checklist: checklistRes.data,
        messages: chatRes.data.messages || [],
        moisture: moistureRes.data.exists ? moistureRes.data : null,
      });
    } catch (err) { console.error(err); toast.error("Failed to load details"); }
    finally { setLoadingDetail(false); }
  };

  // Filter
  const filtered = jobs.filter(j => {
    if (tab === "completed" && j.current_status !== "completed") return false;
    if (tab === "closed" && j.current_status !== "closed") return false;
    if (tab === "active" && !["in_progress", "en_route", "accepted"].includes(j.current_status)) return false;
    if (tab === "other" && ["completed", "closed", "in_progress", "en_route", "accepted"].includes(j.current_status)) return false;
    if (search) {
      const s = search.toLowerCase();
      return (j.request_code || "").toLowerCase().includes(s) ||
             (j.service_type_label || "").toLowerCase().includes(s) ||
             (j.customer_name || "").toLowerCase().includes(s) ||
             (j.property_city || "").toLowerCase().includes(s);
    }
    return true;
  });

  const counts = {
    all: jobs.length,
    active: jobs.filter(j => ["in_progress", "en_route", "accepted"].includes(j.current_status)).length,
    completed: jobs.filter(j => j.current_status === "completed").length,
    closed: jobs.filter(j => j.current_status === "closed").length,
    other: jobs.filter(j => !["completed", "closed", "in_progress", "en_route", "accepted"].includes(j.current_status)).length,
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="history-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-bold text-zinc-900">Job Archive</h2>
        <Badge variant="secondary" className="text-xs">{total} total jobs</Badge>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search jobs by code, service, customer, city..."
          className="w-full pl-9 pr-4 py-2 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
          data-testid="history-search" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1" data-testid="history-tabs">
        {[
          { id: "all", label: "All", icon: FileText },
          { id: "active", label: "Active", icon: Clock },
          { id: "completed", label: "Completed", icon: CheckCircle2 },
          { id: "closed", label: "Closed", icon: Lock },
          { id: "other", label: "Other", icon: XCircle },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
              tab === t.id ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500 hover:text-zinc-700"
            }`} data-testid={`tab-${t.id}`}>
            <t.icon className="w-3 h-3" /> {t.label} ({counts[t.id]})
          </button>
        ))}
      </div>

      {/* Job List */}
      {filtered.length === 0 ? (
        <Card className="border border-zinc-200">
          <CardContent className="p-8 text-center">
            <FileText className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">{search ? "No matching jobs" : "No jobs in this category"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(job => {
            const badge = STATUS_BADGE[job.current_status] || { label: job.current_status, cls: "bg-zinc-100 text-zinc-600" };
            const isExpanded = expandedJob === job.id;

            return (
              <Card key={job.id} className={`border transition-colors ${isExpanded ? "border-zinc-400 shadow-sm" : "border-zinc-200"}`}
                data-testid={`job-archive-${job.id}`}>
                <CardContent className="p-0">
                  {/* Job Header - clickable */}
                  <button onClick={() => loadJobDetail(job.id)}
                    className="w-full p-4 text-left flex items-start gap-3 hover:bg-zinc-50 transition-colors"
                    data-testid={`expand-job-${job.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] text-zinc-400">{job.request_code}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>
                          {badge.label}
                        </span>
                        {job.is_emergency && (
                          <Badge className="bg-red-100 text-red-700 text-[9px] border border-red-200">EMR</Badge>
                        )}
                        {job.customer_rating && (
                          <span className="flex items-center gap-0.5 text-[10px] text-amber-600">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {job.customer_rating}
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-zinc-900">{job.service_type_label}</h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3 text-zinc-400" />
                        <span className="text-xs text-zinc-500 truncate">
                          {job.customer_name && `${job.customer_name} — `}{job.property_address_line1}, {job.property_city}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-zinc-400">
                        <span>{new Date(job.created_at).toLocaleDateString()}</span>
                        {job.completed_at && <span>Done: {new Date(job.completed_at).toLocaleDateString()}</span>}
                        {job.closed_at && <span>Paid: {new Date(job.closed_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400 mt-1" /> : <ChevronDown className="w-4 h-4 text-zinc-400 mt-1" />}
                  </button>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div className="border-t border-zinc-200 p-4 space-y-4 bg-zinc-50/50" data-testid={`job-detail-${job.id}`}>
                      {loadingDetail ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />
                        </div>
                      ) : jobDetail ? (
                        <>
                          {/* Export Button */}
                          <div className="flex justify-end">
                            <JobReportPDF job={job} detail={jobDetail} />
                          </div>
                          {/* Description */}
                          {jobDetail.problem_description && (
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase font-bold mb-1">Description</p>
                              <p className="text-xs text-zinc-600 leading-relaxed">{jobDetail.problem_description}</p>
                            </div>
                          )}

                          {/* Status Timeline */}
                          {jobDetail.status_history?.length > 0 && (
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase font-bold mb-2">Timeline</p>
                              <div className="space-y-1">
                                {jobDetail.status_history.map((sh, i) => (
                                  <div key={i} className="flex items-center gap-2 text-[10px]">
                                    <div className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                                    <span className="text-zinc-500 w-28">{new Date(sh.created_at).toLocaleString()}</span>
                                    <Badge className="text-[9px] bg-zinc-100 text-zinc-600">{sh.new_status}</Badge>
                                    {sh.change_reason && <span className="text-zinc-400 truncate">{sh.change_reason}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Documentation Summary */}
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { icon: Camera, label: "Photos", count: jobDetail.photos?.length || 0, color: "text-blue-600" },
                              { icon: Receipt, label: "Receipts", count: jobDetail.receipts?.length || 0, color: "text-emerald-600" },
                              { icon: MessageSquare, label: "Messages", count: jobDetail.messages?.length || 0, color: "text-purple-600" },
                              { icon: Package, label: "Inventory", count: jobDetail.inventory?.length || 0, color: "text-amber-600" },
                              { icon: DollarSign, label: "Expenses", count: jobDetail.expenses?.length || 0, color: "text-red-600" },
                              { icon: Droplets, label: "Moisture", count: jobDetail.moisture ? "Yes" : "No", color: "text-cyan-600" },
                            ].map((s, i) => (
                              <div key={i} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-zinc-100">
                                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                                <div>
                                  <p className="text-[10px] text-zinc-400">{s.label}</p>
                                  <p className="text-xs font-bold text-zinc-700">{s.count}</p>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Photos */}
                          {jobDetail.photos?.length > 0 && (
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase font-bold mb-2">
                                <Camera className="w-3 h-3 inline mr-1" />Photos ({jobDetail.photos.length})
                              </p>
                              <div className="grid grid-cols-3 gap-1.5">
                                {jobDetail.photos.map(p => (
                                  <div key={p.id} className="bg-white rounded-lg overflow-hidden border border-zinc-100">
                                    <img src={`${API_BASE}/api/photos/image/${p.id}`} alt="Job photo"
                                      className="w-full h-20 object-cover" loading="lazy" />
                                    <div className="p-1.5">
                                      {p.ai_description && (
                                        <p className="text-[9px] text-zinc-500 line-clamp-2">{p.ai_description}</p>
                                      )}
                                      <div className="flex items-center justify-between mt-0.5">
                                        {p.is_approved && <Badge className="text-[8px] bg-emerald-100 text-emerald-700">Approved</Badge>}
                                        <span className="text-[8px] text-zinc-400">{new Date(p.created_at).toLocaleTimeString()}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Receipts */}
                          {jobDetail.receipts?.length > 0 && (
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase font-bold mb-1">
                                <Receipt className="w-3 h-3 inline mr-1" />Receipts
                              </p>
                              {jobDetail.receipts.map(r => (
                                <div key={r.id} className="flex items-center justify-between p-2 bg-white rounded border border-zinc-100 mb-1">
                                  <span className="text-xs text-zinc-600">{r.items || "Receipt"}</span>
                                  <span className="text-xs font-bold text-emerald-700">${r.amount?.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Inventory */}
                          {jobDetail.inventory?.length > 0 && (
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase font-bold mb-1">
                                <Package className="w-3 h-3 inline mr-1" />Inventory (${jobDetail.inventoryCost})
                              </p>
                              <div className="space-y-1">
                                {jobDetail.inventory.map(item => (
                                  <div key={item.id} className="flex items-center justify-between p-2 bg-white rounded border border-zinc-100">
                                    <div>
                                      <span className="text-xs text-zinc-700 font-medium">{item.name}</span>
                                      <span className="text-[10px] text-zinc-400 ml-2">{item.quantity} {item.unit}</span>
                                      {item.left_on_site && <Badge className="text-[8px] bg-amber-100 text-amber-700 ml-1">On Site</Badge>}
                                    </div>
                                    <span className="text-xs font-medium text-zinc-600">${item.total_cost?.toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Expenses */}
                          {jobDetail.expenses?.length > 0 && (
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase font-bold mb-1">
                                <DollarSign className="w-3 h-3 inline mr-1" />Expenses
                              </p>
                              {jobDetail.expenses.map(e => (
                                <div key={e.id} className="flex items-center justify-between p-2 bg-white rounded border border-zinc-100 mb-1">
                                  <div>
                                    <span className="text-xs text-zinc-600">{e.description || e.category}</span>
                                    {e.mileage > 0 && <span className="text-[10px] text-zinc-400 ml-2">{e.mileage} mi</span>}
                                  </div>
                                  <span className="text-xs font-bold text-red-600">${e.amount?.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Checklist */}
                          {jobDetail.checklist?.items?.length > 0 && (
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase font-bold mb-1">
                                <ClipboardCheck className="w-3 h-3 inline mr-1" />Checklist ({jobDetail.checklist.progress}% complete)
                              </p>
                              <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden mb-2">
                                <div className={`h-full rounded-full ${jobDetail.checklist.progress >= 100 ? "bg-emerald-500" : "bg-amber-500"}`}
                                  style={{ width: `${jobDetail.checklist.progress}%` }} />
                              </div>
                              <div className="space-y-0.5">
                                {jobDetail.checklist.items.map((item, i) => (
                                  <div key={i} className="flex items-center gap-2 text-[10px]">
                                    {item.checked
                                      ? <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                                      : <div className="w-3 h-3 rounded-full border border-zinc-300 flex-shrink-0" />}
                                    <span className={item.checked ? "text-zinc-400 line-through" : "text-zinc-600"}>{item.label}</span>
                                    {item.required && !item.checked && <span className="text-[8px] text-red-500 font-bold">REQ</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Moisture Map Summary */}
                          {jobDetail.moisture && (
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase font-bold mb-1">
                                <Droplets className="w-3 h-3 inline mr-1" />Moisture Map — {jobDetail.moisture.room}
                              </p>
                              <div className="grid grid-cols-3 gap-1.5">
                                {(jobDetail.moisture.readings || []).map(r => (
                                  <div key={r.id} className="p-2 bg-white rounded border border-zinc-100 text-center">
                                    <p className="text-xs font-mono font-bold" style={{ color: r.zone === "Dry" ? "#22c55e" : r.zone === "Elevated" ? "#eab308" : r.zone === "High" ? "#f97316" : r.zone === "Critical" ? "#ef4444" : "#991b1b" }}>
                                      {r.pct}%
                                    </p>
                                    <p className="text-[9px] text-zinc-500">{r.material}</p>
                                    <p className="text-[8px] text-zinc-400">{r.pointLabel} - {r.zone}</p>
                                  </div>
                                ))}
                              </div>
                              {jobDetail.moisture.ai_analysis && (
                                <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-100">
                                  <p className="text-[10px] text-purple-700 font-bold">AI Assessment: {jobDetail.moisture.ai_analysis.severity}</p>
                                  <p className="text-[10px] text-purple-600 mt-0.5">{jobDetail.moisture.ai_analysis.summary?.substring(0, 150)}...</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Chat Log */}
                          {jobDetail.messages?.length > 0 && (
                            <div>
                              <p className="text-[10px] text-zinc-400 uppercase font-bold mb-1">
                                <MessageSquare className="w-3 h-3 inline mr-1" />Chat Log ({jobDetail.messages.length} messages)
                              </p>
                              <div className="max-h-32 overflow-y-auto space-y-1 p-2 bg-white rounded border border-zinc-100">
                                {jobDetail.messages.map(m => (
                                  <div key={m.id} className="text-[10px]">
                                    <span className={`font-bold ${m.sender_type === "technician" ? "text-zinc-700" : m.sender_type === "homeowner" ? "text-blue-600" : "text-purple-600"}`}>
                                      {m.sender_name}:
                                    </span>
                                    <span className="text-zinc-500 ml-1">{m.text}</span>
                                    <span className="text-zinc-300 ml-1">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Payment Notes */}
                          {jobDetail.payment_notes && (
                            <div className="p-2 bg-emerald-50 rounded border border-emerald-100">
                              <p className="text-[10px] text-emerald-700 font-bold">Payment Notes</p>
                              <p className="text-xs text-emerald-600">{jobDetail.payment_notes}</p>
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
